import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Input, message, PageHeader, Row, Space, Table, Typography, Upload } from 'antd';
import { Provider } from 'jotai';
import { useRendimientoPage } from './hooks';
import { LoginService } from 'services/LoginService';
import { Headers, TipoUsuario } from 'enums';
import { ClearOutlined, FileExcelOutlined, SearchOutlined, UploadOutlined } from "@ant-design/icons";

const RendimientoPage = () => {
    return (
        <Provider>
            <Navegacion />
            <Filtros />
            <StatusImportacion />
            <Card bordered={false}>
                <Botones />
                <ListaRendimiento />
            </Card>
        </Provider>
    );
};

const Filtros = () => {

    const { fetchRendimiento } = useRendimientoPage();

    const [filters, setFilters] = useState({
        numeroDocumentoIdentidad: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        nombres: ''
    });

    const handleInputChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        fetchRendimiento(filters);
    };

    const handleClear = () => {
        const params = {
            numeroDocumentoIdentidad: '',
            apellidoPaterno: '',
            apellidoMaterno: '',
            nombres: ''
        };
        setFilters(params);
        fetchRendimiento(params);
    };

    return (
        <Card bordered={false} style={{ marginBottom: "10px" }}>
            <Row align="middle" gutter={[16, 16]}>
                <>
                    <Col xs={24} sm={8} md={5}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <span style={{ fontWeight: 500 }}>DNI:</span>
                            <Input
                                placeholder="Ingrese valor"
                                value={filters.numeroDocumentoIdentidad}
                                onChange={(e) => handleInputChange('numeroDocumentoIdentidad', e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </Space>
                    </Col>

                    <Col xs={24} sm={8} md={5}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <span style={{ fontWeight: 500 }}>Apellido Paterno:</span>
                            <Input
                                placeholder="Ingrese valor"
                                value={filters.apellidoPaterno}
                                onChange={(e) => handleInputChange('apellidoPaterno', e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </Space>
                    </Col>

                    <Col xs={24} sm={8} md={5}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <span style={{ fontWeight: 500 }}>Apellido Materno:</span>
                            <Input
                                placeholder="Ingrese valor"
                                value={filters.apellidoMaterno}
                                onChange={(e) => handleInputChange('apellidoMaterno', e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </Space>
                    </Col>

                    <Col xs={24} sm={8} md={5}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <span style={{ fontWeight: 500 }}>Nombres:</span>
                            <Input
                                placeholder="Ingrese valor"
                                value={filters.nombres}
                                onChange={(e) => handleInputChange('nombres', e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </Space>
                    </Col>

                    <Col xs={24} sm={24} md={4}>
                        <Space style={{ marginTop: '24px', float: 'right' }}>
                            <Button onClick={handleClear} icon={<ClearOutlined />}>
                                Limpiar
                            </Button>
                            <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                                Buscar
                            </Button>
                        </Space>
                    </Col>
                </>
            </Row>
        </Card>
    );
};

const StatusImportacion = () => {
    const { dnisNoProcesados } = useRendimientoPage();

    if (dnisNoProcesados.length === 0) {
        return "";
    }

    return (
        <Alert
            message="Error"
            description={`Los siguientes DNIs no han sido procesados: ${dnisNoProcesados?.join(', ')}`}
            type="error"
            showIcon
            style={{ marginBottom: "10px" }}
        />
    );
};

const Botones = () => {

    const { fetchRendimiento, exportar, loading: loadingExportar, setDnisNoProcesados } = useRendimientoPage();
    const [loading, setLoading] = useState(false);

    const propsBtnImportar = {
        name: "excel",
        action: process.env.REACT_APP_ENDPOINT + "admin/comunicaciones-cultura/rendimiento/importar",
        showUploadList: "false",
        headers: {
            ["authorization"]: "Bearer " + LoginService.getJwtToken(),
            [Headers.ORGANIZACION_ID]: LoginService.getUsuarioAutenticado().organizacionId,
            [Headers.EMPRESA_ID]: LoginService.getUsuarioAutenticado().empresaSeleccionada.id,
            [Headers.TIPO_USUARIO]: TipoUsuario.ADMIN
        },
        onChange(info) {
            if (info.file.status === 'uploading') {
                setLoading(true);
            }
            if (info.file.status !== 'uploading') {
                fetchRendimiento();
            }
            if (info.file.status === 'done') {
                setDnisNoProcesados(info.file.response.dnisNoProcesados);
                if (info.file.response.dnisNoProcesados.length > 0) {
                    message.success(`Se subió el rendimiento con algunas observaciones`);
                } else {
                    message.success(`Se subió el rendimiento correctamente`);
                }
                setLoading(false);
            } else if (info.file.status === 'error') {
                message.error(`No se ha podido subir el rendimiento. Intente nuevamente.`);
                setLoading(false);
            }
        },
    };

    return (
        <Row justify="end" style={{ marginBottom: "10px" }}>
            <Space>
                <Upload {...propsBtnImportar} maxCount={1} itemRender={() => ("")}>
                    <Button icon={<UploadOutlined />} loading={loading}>
                        Subir Rendimiento
                    </Button>
                </Upload>

                <Button onClick={exportar} loading={loadingExportar}>
                    <FileExcelOutlined /> Exportar
                </Button>
            </Space>
        </Row>
    );
};

const ListaRendimiento = () => {

    const { rendimiento, fetchRendimiento } = useRendimientoPage();

    useEffect(() => {
        fetchRendimiento();
    }, []);

    const columnas = [
        {
            title: "Colaborador",
            render: (fila) =>
                <Row direction="vertical">
                    <Col span={24}>
                        <Typography.Text>
                            {fila.colaborador.apellidoPaterno} {fila.colaborador.apellidoMaterno} {fila.colaborador.nombres}
                        </Typography.Text>
                    </Col>
                    <Col span={24}>
                        <Typography.Text type='secondary'>
                            {fila.colaborador.numeroDocumentoIdentidad}
                        </Typography.Text>
                    </Col>
                </Row>
        },
        {
            title: "Labor",
            dataIndex: "labor",
            width: '15%',
        },
        {
            title: "Año",
            dataIndex: "anio",
            width: '8%',
        },
        {
            title: "Fecha",
            dataIndex: "fecha",
            width: '12%',
            render: (fecha) => {
                if (!fecha) return '';
                const [anio, mes, dia] = fecha.split('-');
                return `${dia}/${mes}/${anio}`;
            }
        },
        {
            title: "Medición",
            dataIndex: "medicion",
            width: '12%',
        },
        {
            title: "Rendimiento",
            dataIndex: "rendimiento",
            width: '12%',
        },
    ];

    return (
        <Table
            rowKey={(registro) => registro.id}
            dataSource={rendimiento}
            columns={columnas}
            size='small'
        />
    );
};

const Navegacion = () => {
    return (
        <PageHeader
            title="Rendimiento"
            breadcrumb={{
                routes: [
                    { breadcrumbName: "Comunicación y Cultura" },
                    { breadcrumbName: 'Rendimiento' }
                ],
            }}
            style={{ paddingLeft: "0px" }}
        />
    );
};

export default RendimientoPage;
